/**
 * TripBriefing — print-friendly trip briefing for the currently loaded itinerary.
 *
 * Shown as a full-screen overlay in Explore mode. Fetches an aggregated
 * briefing from /briefing/generate and renders it as a series of cards that
 * print cleanly on letter paper. Includes PDF / Word / print actions.
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  XMarkIcon, PrinterIcon, DocumentArrowDownIcon, ArrowPathIcon,
  MapPinIcon, ClockIcon, CloudIcon, ExclamationTriangleIcon,
  BuildingOffice2Icon, GlobeAltIcon,
} from '@heroicons/react/24/outline';
import { briefingAPI, type TripBriefingData } from '../../services/api';

// ─── helpers ─────────────────────────────────────────────────────────────────

function fmtDate(iso: string | undefined): string {
  if (!iso) return '—';
  try {
    return new Date((iso.length === 10 ? iso + 'T12:00' : iso))
      .toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
}
function fmtDT(iso: string | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch { return iso; }
}
function fmtDur(min?: number): string {
  if (!min) return '—';
  const h = Math.floor(min / 60), m = Math.round(min % 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}
function modeEmoji(mode?: string): string {
  return ({ drive: '🚗', fly: '✈️', train: '🚆', bus: '🚌', ferry: '⛴️' } as Record<string, string>)[mode || ''] || '🛣️';
}

// ─── main component ─────────────────────────────────────────────────────────

export interface TripBriefingProps {
  itineraryId?: string;
  itineraryData?: any;        // fallback when the trip isn't server-persisted yet
  tripTitle: string;
  onClose: () => void;
}

export const TripBriefing: React.FC<TripBriefingProps> = ({
  itineraryId, itineraryData, tripTitle, onClose,
}) => {
  const [briefing, setBriefing] = useState<TripBriefingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const b = await briefingAPI.generate(
        itineraryId ? { itinerary_id: itineraryId } : { itinerary_data: itineraryData },
      );
      setBriefing(b);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to build briefing');
    } finally {
      setLoading(false);
    }
  }, [itineraryId, itineraryData]);

  useEffect(() => { load(); }, [load]);

  const download = useCallback(async (format: 'pdf' | 'docx' | 'txt') => {
    if (!briefing) return;
    setDownloading(format);
    try {
      const blob = await briefingAPI.exportDocument(briefing, format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tripTitle || 'Trip'} — Briefing.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(`Download failed: ${e?.message || 'Unknown error'}`);
    } finally {
      setDownloading(null);
    }
  }, [briefing, tripTitle]);

  const printNow = () => window.print();

  return (
    <div className="fixed inset-0 z-[2000] bg-white dark:bg-gray-900 overflow-y-auto">
      {/* Action bar — hidden in print */}
      <div className="sticky top-0 z-10 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-b border-gray-200 dark:border-gray-700 px-4 py-2.5 flex items-center gap-3 print:hidden">
        <button onClick={onClose} className="p-1.5 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white" title="Close">
          <XMarkIcon className="w-5 h-5" />
        </button>
        <h1 className="flex-1 text-sm md:text-base font-semibold text-gray-900 dark:text-white truncate">
          Trip Briefing — {tripTitle}
        </h1>

        <button onClick={load} disabled={loading}
          className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50">
          <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
        <button onClick={printNow} disabled={!briefing}
          className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50">
          <PrinterIcon className="w-4 h-4" />
          <span className="hidden sm:inline">Print</span>
        </button>
        <button onClick={() => download('pdf')} disabled={!briefing || !!downloading}
          className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-50">
          {downloading === 'pdf' ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <DocumentArrowDownIcon className="w-4 h-4" />}
          PDF
        </button>
        <button onClick={() => download('docx')} disabled={!briefing || !!downloading}
          className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
          {downloading === 'docx' ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <DocumentArrowDownIcon className="w-4 h-4" />}
          Word
        </button>
      </div>

      {/* Body */}
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-6 print:py-0 print:px-0 text-gray-900 dark:text-gray-100">
        {loading && (
          <div className="text-center py-20">
            <ArrowPathIcon className="w-10 h-10 text-teal-500 animate-spin mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Pulling together weather, visa info, culture, phrases, and packing list…
            </p>
            <p className="text-xs text-gray-400 mt-1">This can take 15–30 seconds on longer trips.</p>
          </div>
        )}

        {error && !loading && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg flex items-start gap-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-700 dark:text-red-300">Could not build briefing</p>
              <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">{error}</p>
            </div>
          </div>
        )}

        {briefing && <BriefingContent b={briefing} />}
      </div>

      {/* Print-specific styles */}
      <style>{`
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          .page-break { page-break-before: always; }
          .brief-card { break-inside: avoid; }
          a { color: #0891b2; text-decoration: none; }
        }
      `}</style>
    </div>
  );
};

// ─── content renderer ────────────────────────────────────────────────────────

const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode; breakBefore?: boolean }> = ({
  title, subtitle, children, breakBefore,
}) => (
  <section className={`brief-card mb-8 ${breakBefore ? 'page-break' : ''}`}>
    <h2 className="text-xl font-bold text-teal-700 dark:text-teal-300 mb-1 border-b-2 border-teal-600 pb-1">{title}</h2>
    {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{subtitle}</p>}
    <div className="mt-3">{children}</div>
  </section>
);

const BriefingContent: React.FC<{ b: TripBriefingData }> = ({ b }) => {
  const ov = b.overview;

  return (
    <>
      {/* Title block */}
      <header className="mb-6 brief-card">
        <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white">{b.trip_title}</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {fmtDate(ov.start_date)} → {fmtDate(ov.end_date)} · {ov.total_nights} night{ov.total_nights !== 1 ? 's' : ''} · {(ov.countries || []).join(', ')}
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
          <strong>Route:</strong> {(ov.cities || []).join(' → ')}
        </p>
        {ov.total_drive_km > 0 && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Driving total: {Math.round(ov.total_drive_km)} km · {fmtDur(ov.total_drive_minutes)}
          </p>
        )}
        <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2">
          Generated {b.generated_at?.slice(0, 10)} · Verify all details with official sources before travel.
        </p>
      </header>

      {/* Per-stay briefings */}
      <Section title="Destination briefings" subtitle="Top sights, where to sleep, where to eat — one card per stop.">
        <div className="space-y-5">
          {b.stays.map((s, i) => (
            <div key={i} className="brief-card rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-gray-50/40 dark:bg-gray-800/40">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    {i + 1}. {s.city}, {s.country}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {fmtDate(s.check_in)} → {fmtDate(s.check_out)} · {s.nights} night{s.nights !== 1 ? 's' : ''}
                    {s.check_in && (
                      <> · <ClockIcon className="inline w-3 h-3 mb-0.5" /> Check-in {new Date(s.check_in).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</>
                    )}
                  </p>
                </div>
                {!s.enriched && (
                  <span className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">
                    Not yet enriched
                  </span>
                )}
              </div>

              {s.highlights.length > 0 && (
                <div className="mb-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">🗺 Top sights</p>
                  <ul className="space-y-1">
                    {s.highlights.map((h, j) => (
                      <li key={j} className="text-sm">
                        <span className="font-semibold text-gray-900 dark:text-white">{h.title}</span>
                        {h.time_slot && <span className="text-xs text-gray-400 ml-1">({h.time_slot})</span>}
                        {h.duration_hours && <span className="text-xs text-gray-400 ml-1">· {h.duration_hours}h</span>}
                        {(h.estimated_cost_usd || 0) > 0 && <span className="text-xs text-gray-400 ml-1">· ~${h.estimated_cost_usd}</span>}
                        {h.description && <p className="text-xs text-gray-600 dark:text-gray-300 ml-2">{h.description}</p>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {s.lodging && (
                <div className="mb-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">🏨 Lodging pick</p>
                  <p className="text-sm">
                    <span className="font-semibold text-gray-900 dark:text-white">{s.lodging.name}</span>
                    <span className="text-xs text-gray-500 ml-1">
                      ({s.lodging.type} · {s.lodging.area}, <em>{s.lodging.price_level}</em>)
                    </span>
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">{s.lodging.description}</p>
                </div>
              )}

              {s.dining && s.dining.length > 0 && (
                <div className="mb-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">🍽 Dining picks</p>
                  <ul className="space-y-1">
                    {s.dining.map((d, j) => (
                      <li key={j} className="text-sm">
                        <span className="font-semibold text-gray-900 dark:text-white">{d.name}</span>
                        <span className="text-xs text-gray-500 ml-1">
                          ({d.cuisine} · {d.area}, <em>{d.price_level}</em>)
                        </span>
                        <p className="text-xs text-gray-600 dark:text-gray-300 ml-2">{d.description}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {s.travel_tips && s.travel_tips.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">💡 Local tips</p>
                  <ul className="space-y-0.5">
                    {s.travel_tips.map((t, j) => (
                      <li key={j} className="text-xs text-gray-600 dark:text-gray-300">• {t}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </Section>

      {/* Transit */}
      {b.transits.length > 0 && (
        <Section title="Transit legs">
          <div className="space-y-2">
            {b.transits.map((t, i) => (
              <div key={i} className="brief-card border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {modeEmoji(t.mode)} {t.from_city} → {t.to_city}
                  <span className="text-xs text-gray-500 font-normal ml-2">
                    {t.mode}, {fmtDur(t.duration_minutes)}
                    {t.distance_km ? `, ${Math.round(t.distance_km)} km` : ''}
                  </span>
                </p>
                {(t.departure_time || t.arrival_time) && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {fmtDT(t.departure_time)} → {fmtDT(t.arrival_time)}
                  </p>
                )}
                {t.route_sights.length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {t.route_sights.map((rs, j) => (
                      <li key={j} className="text-xs text-amber-700 dark:text-amber-400 ml-4">
                        ↪ <strong>{rs.name}</strong>{rs.why && <span className="text-amber-600"> — {rs.why}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Weather */}
      {b.weather.some(w => w.available || w.too_far) && (
        <Section title="Weather forecast" subtitle="From Open-Meteo, covering your actual travel dates.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {b.weather.map((w, i) => (
              <div key={i} className="brief-card border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <CloudIcon className="w-4 h-4 text-sky-500" />
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{w.city}</p>
                </div>
                {w.too_far ? (
                  <p className="text-xs text-gray-500 italic">Forecast unavailable — trip is more than 16 days out.</p>
                ) : !w.available ? (
                  <p className="text-xs text-gray-400 italic">Forecast unavailable.</p>
                ) : (
                  <>
                    <p className="text-xs text-gray-500">{w.date_range}</p>
                    <p className="text-sm text-gray-700 dark:text-gray-200 mt-1">
                      <strong>{w.temp_c_min}°C → {w.temp_c_max}°C</strong>
                      {w.precip_days !== undefined && w.total_days && (
                        <span className="text-xs text-gray-500 ml-2">
                          {w.precip_days}/{w.total_days} rainy day{w.precip_days !== 1 ? 's' : ''}
                        </span>
                      )}
                    </p>
                  </>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Country entry / money / emergencies */}
      {b.countries.length > 0 && (
        <Section title="Entry, money & emergencies" breakBefore>
          <div className="space-y-3">
            {b.countries.map((c, i) => (
              <div key={i} className="brief-card border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <GlobeAltIcon className="w-4 h-4 text-teal-600" />
                  <p className="text-base font-bold text-gray-900 dark:text-white">{c.country}</p>
                  {c.schengen_member && <span className="text-[10px] bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded">Schengen</span>}
                  {c.eu_member && !c.schengen_member && <span className="text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">EU</span>}
                  {c.safety_rating && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      c.safety_rating?.toLowerCase().includes('safe')
                        ? 'bg-green-100 text-green-700'
                        : c.safety_rating?.toLowerCase().includes('caution')
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {c.safety_rating}
                    </span>
                  )}
                </div>
                <dl className="text-xs text-gray-700 dark:text-gray-300 space-y-0.5">
                  {c.currency && <div><dt className="inline font-semibold">Currency:</dt> {c.currency}</div>}
                  {c.language && <div><dt className="inline font-semibold">Language:</dt> {c.language}</div>}
                  {c.visa_info && <div><dt className="inline font-semibold">Visa / entry:</dt> {c.visa_info}</div>}
                  {c.entry_requirements && <div><dt className="inline font-semibold">Entry:</dt> {c.entry_requirements}</div>}
                  {c.safety_notes && <div><dt className="inline font-semibold">Safety:</dt> {c.safety_notes}</div>}
                  {c.emergency_number && <div><dt className="inline font-semibold">Emergency:</dt> <strong className="text-red-600 dark:text-red-400">{c.emergency_number}</strong></div>}
                  {c.practical_tips && c.practical_tips.length > 0 && (
                    <div>
                      <dt className="font-semibold">Tips:</dt>
                      <ul className="list-disc list-inside ml-2">
                        {c.practical_tips.slice(0, 4).map((t, j) => <li key={j}>{t}</li>)}
                      </ul>
                    </div>
                  )}
                  {c.advisory_url && (
                    <div><dt className="inline font-semibold">Advisory:</dt> <a href={c.advisory_url} target="_blank" rel="noopener noreferrer" className="text-teal-600 underline">{c.advisory_url}</a></div>
                  )}
                </dl>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Cultural */}
      {b.culture.some(c => c.greetings || c.tipping || (c.dos && c.dos.length)) && (
        <Section title="Cultural quick-reference">
          <div className="space-y-3">
            {b.culture.map((c, i) => {
              const hasAny = c.greetings || c.tipping || c.dining_note || (c.dos && c.dos.length) || (c.donts && c.donts.length);
              if (!hasAny) return null;
              return (
                <div key={i} className="brief-card border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                  <p className="text-base font-bold text-gray-900 dark:text-white mb-1">{c.country}</p>
                  {c.greetings && <p className="text-sm text-gray-700 dark:text-gray-300"><strong>Greetings:</strong> {c.greetings}</p>}
                  {c.tipping && <p className="text-sm text-gray-700 dark:text-gray-300 mt-1"><strong>Tipping:</strong> {c.tipping}</p>}
                  {c.dining_note && <p className="text-sm text-gray-700 dark:text-gray-300 mt-1"><strong>Dining custom:</strong> {c.dining_note}</p>}
                  {(c.dos || []).length > 0 && (
                    <ul className="mt-2 space-y-0.5">
                      {c.dos!.map((d, j) => <li key={j} className="text-xs text-green-700 dark:text-green-400">✓ {d}</li>)}
                    </ul>
                  )}
                  {(c.donts || []).length > 0 && (
                    <ul className="mt-1 space-y-0.5">
                      {c.donts!.map((d, j) => <li key={j} className="text-xs text-red-700 dark:text-red-400">✗ {d}</li>)}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* Phrases */}
      {b.phrases.some(p => p.phrases.length > 0) && (
        <Section title="Essential phrases" breakBefore>
          <div className="space-y-3">
            {b.phrases.filter(p => p.phrases.length > 0).map((p, i) => (
              <div key={i} className="brief-card border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                <p className="text-base font-bold text-gray-900 dark:text-white mb-1">
                  {p.country} <span className="text-xs text-gray-500 font-normal">({p.language})</span>
                </p>
                <table className="text-sm w-full">
                  <tbody>
                    {p.phrases.map((ph, j) => (
                      <tr key={j} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                        <td className="py-1 pr-2 text-gray-600 dark:text-gray-400 w-1/3">{ph.english}</td>
                        <td className="py-1 pr-2 font-semibold text-gray-900 dark:text-white">{ph.local}</td>
                        <td className="py-1 text-xs text-gray-500 italic">{ph.pronunciation}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Packing */}
      {b.packing.categories.length > 0 && (
        <Section title="Packing list" subtitle="Tailored to your destinations, dates, and interests.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {b.packing.categories.map((cat, i) => (
              <div key={i} className="brief-card border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                <p className="text-sm font-bold text-gray-900 dark:text-white mb-1">{cat.name}</p>
                <ul className="space-y-0.5">
                  {cat.items.map((it, j) => (
                    <li key={j} className="text-xs text-gray-700 dark:text-gray-300 flex items-start gap-1">
                      <span className="inline-block w-3 h-3 border border-gray-400 rounded-sm mt-0.5 flex-shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Daily agenda */}
      {b.agenda.length > 0 && (
        <Section title="Daily agenda" breakBefore>
          <div className="space-y-1.5">
            {b.agenda.map((d, i) => (
              <div key={i} className="brief-card flex items-start gap-2 border-l-4 pl-2 py-1 border-teal-600/40">
                <div className="w-24 flex-shrink-0 text-xs text-gray-500 dark:text-gray-400">
                  {fmtDate(d.date)}
                </div>
                <div className="flex-1 min-w-0">
                  {d.kind === 'transit' ? (
                    <p className="text-sm text-gray-800 dark:text-gray-200">
                      {modeEmoji(d.mode)} {d.title} <span className="text-xs text-gray-500">· {fmtDur(d.duration_minutes)}</span>
                    </p>
                  ) : (
                    <>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {d.title}
                        {d.check_in_time && (
                          <span className="text-xs text-gray-500 font-normal ml-2">
                            · Check-in {new Date(d.check_in_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                          </span>
                        )}
                      </p>
                      {(d.activities || []).map((a, j) => (
                        <p key={j} className="text-xs text-gray-600 dark:text-gray-300 ml-1">
                          <span className="italic">{a.time_slot}</span>: {a.title} <span className="text-gray-400">({a.duration_hours}h)</span>
                        </p>
                      ))}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}
    </>
  );
};
